import * as React from "react";
import { useLiveDashboard, AgentData, TeamData, TopAgent, ChannelBreakdown } from "@/hooks/useLiveDashboard";

// ── Status dot helpers ────────────────────────────────────────────────────────

function statusColor(status: string, currentChats: number, maxChats: number): string {
  if (status === "Offline") return "bg-slate-400";
  if (status === "Available") return "bg-success-green-500";
  if (status === "Busy" || (maxChats > 0 && currentChats >= maxChats)) return "bg-red-500";
  return "bg-sweet-orange-500"; // Away / partial capacity
}

function StatusDot({ status, currentChats, maxChats }: { status: string; currentChats: number; maxChats: number }) {
  const colorClass = statusColor(status, currentChats, maxChats);
  return (
    <span
      aria-label={`Status: ${status}`}
      className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${colorClass}`}
    />
  );
}

// ── Connection bar ────────────────────────────────────────────────────────────

function ConnectionBar({
  status,
  lastUpdated,
  isManagerView
}: {
  status: "connecting" | "live" | "reconnecting" | "error";
  lastUpdated: Date | null;
  isManagerView: boolean;
}) {
  const dotClass =
    status === "live" ? "bg-green-500" :
    status === "reconnecting" ? "bg-amber-500" :
    "bg-red-500";

  const label =
    status === "live" ? "Live" :
    status === "reconnecting" ? "Reconnecting..." :
    status === "connecting" ? "Connecting..." :
    "Connection lost";

  return (
    <div className="flex items-center justify-between px-4 py-2 rounded-lg mb-4 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={`inline-block w-2.5 h-2.5 rounded-full ${dotClass} ${status === "live" ? "animate-pulse" : ""}`}
        />
        <span className="text-slate-600 dark:text-slate-300">{label}</span>
        {lastUpdated && status === "live" && (
          <span className="text-slate-400 dark:text-slate-500">
            &middot; Last updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>
      {isManagerView && (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
          My Teams
        </span>
      )}
    </div>
  );
}

// ── KPI strip ─────────────────────────────────────────────────────────────────

function InfoIcon({ tip }: { tip: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label="More information"
        className="text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-full"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg bg-slate-800 dark:bg-slate-950 text-white text-xs px-3 py-2 shadow-lg pointer-events-none"
        >
          {tip}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800 dark:border-t-slate-950" aria-hidden="true" />
        </span>
      )}
    </span>
  );
}

function KpiCard({ label, value, sub, warn, color, tip }: { label: string; value: string | number; sub?: string; warn?: boolean; color?: "green" | "red" | "amber" | "blue"; tip?: string }) {
  const valueClass =
    color === "green" ? "text-green-600 dark:text-green-400" :
    color === "red" ? "text-red-600 dark:text-red-400" :
    color === "amber" ? "text-amber-600 dark:text-amber-400" :
    color === "blue" ? "text-blue-600 dark:text-blue-400" :
    warn ? "text-amber-600 dark:text-amber-400" :
    "text-slate-800 dark:text-slate-100";

  return (
    <div className={`rounded-lg p-4 flex flex-col bg-white dark:bg-slate-800 border ${warn ? "border-amber-300 dark:border-amber-600" : "border-slate-200 dark:border-slate-700"}`}>
      <span className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
        {tip && <InfoIcon tip={tip} />}
      </span>
      <span
        className={`font-bold mt-1 ${valueClass} ${String(value).length > 5 ? "text-xl" : "text-3xl"}`}
        title={String(value)}
      >
        {value}
      </span>
      {sub && <span className="text-xs mt-0.5 text-slate-400 dark:text-slate-500">{sub}</span>}
    </div>
  );
}

function formatMinutes(mins: number): string {
  if (mins < 1) return "< 1m";
  const m = Math.floor(mins);
  const s = Math.round((mins - m) * 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// ── Agent row ─────────────────────────────────────────────────────────────────

function AgentRow({ agent }: { agent: AgentData }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <StatusDot status={agent.status} currentChats={agent.currentChats} maxChats={agent.maxChats} />
      <span className="flex-1 min-w-0 truncate text-sm text-slate-700 dark:text-slate-300">
        {agent.displayName}
      </span>
      <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
        {agent.currentChats}/{agent.maxChats || "∞"}
      </span>
    </div>
  );
}

// ── Top Agents widget ─────────────────────────────────────────────────────────

function TopAgentsWidget({ agents }: { agents: TopAgent[] }) {
  if (agents.length === 0) return null;
  return (
    <div className="rounded-lg p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">
        Top Agents Today
      </h3>
      <div className="space-y-2">
        {agents.map((a, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-400 dark:text-slate-500 w-4 text-right">{i + 1}</span>
            <span className="flex-1 min-w-0 truncate text-sm text-slate-700 dark:text-slate-300">{a.name}</span>
            <span className="text-xs text-green-600 dark:text-green-400 font-medium tabular-nums">{a.resolved} resolved</span>
            {a.active > 0 && (
              <span className="text-xs text-blue-500 dark:text-blue-400 tabular-nums">{a.active} active</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Channel Breakdown widget ──────────────────────────────────────────────────

const CHANNEL_COLORS: Record<string, string> = {
  Web: "bg-blue-500",
  SMS: "bg-green-500",
  WhatsApp: "bg-emerald-500",
  Facebook: "bg-indigo-500",
  "Twitter (X)": "bg-sky-500",
  IVR: "bg-violet-500",
  Email: "bg-amber-500",
  Ticket: "bg-orange-500",
};

function ChannelBreakdownWidget({ channels }: { channels: ChannelBreakdown[] }) {
  if (channels.length === 0) return null;
  const total = channels.reduce((s, c) => s + c.count, 0);
  return (
    <div className="rounded-lg p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">
        Active by Channel
      </h3>
      <div className="space-y-2">
        {channels.map((c) => {
          const pct = total > 0 ? Math.round((c.count / total) * 100) : 0;
          const barColor = CHANNEL_COLORS[c.channel] ?? "bg-slate-400";
          return (
            <div key={c.channel}>
              <div className="flex items-center justify-between text-xs mb-0.5">
                <span className="text-slate-700 dark:text-slate-300">{c.channel}</span>
                <div className="flex items-center gap-2">
                  {c.queued > 0 && (
                    <span className="text-amber-600 dark:text-amber-400">{c.queued} queued</span>
                  )}
                  <span className="text-slate-500 dark:text-slate-400 tabular-nums">{c.count} ({pct}%)</span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${barColor}`}
                  style={{ width: `${pct}%` }}
                  aria-hidden="true"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Coverage badge ────────────────────────────────────────────────────────────

function CoverageBadge({ status, unassigned }: { status: string; unassigned: number }) {
  if (status === "covered") return null;

  if (status === "no_agents") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700"
        title="No agents are logged in to this team"
      >
        <span aria-hidden="true">⚠</span> No agents online
      </span>
    );
  }

  // no_coverage — online but none available
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700"
      title="Agents are online but all are busy — no one is taking new chats"
    >
      <span aria-hidden="true">⚠</span> All agents busy
      {unassigned > 0 && ` · ${unassigned} waiting`}
    </span>
  );
}

function TeamCard({
  team,
  warnYellowMins,
  warnRedMins,
  showWarnings
}: {
  team: TeamData;
  warnYellowMins: number;
  warnRedMins: number;
  showWarnings: boolean;
}) {
  const [expanded, setExpanded] = React.useState(true);

  const hasAgents = team.agents.length > 0;
  const hasCoverageGap = team.coverageStatus !== "covered";

  const borderClass = hasCoverageGap
    ? team.coverageStatus === "no_agents"
      ? "border-red-400 dark:border-red-600"
      : "border-amber-400 dark:border-amber-600"
    : !showWarnings
    ? "border-slate-200 dark:border-slate-700"
    : team.longestWaitMinutes > warnRedMins
    ? "border-red-300 dark:border-red-700"
    : team.longestWaitMinutes > warnYellowMins
    ? "border-amber-300 dark:border-amber-600"
    : "border-slate-200 dark:border-slate-700";

  return (
    <div className={`rounded-lg p-4 flex flex-col gap-2 bg-white dark:bg-slate-800 border-2 ${borderClass}`}>
      {/* Card header */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base text-slate-800 dark:text-slate-100 truncate">
            {team.teamName}
          </h3>
          <div className="flex flex-wrap gap-2 mt-1 items-center">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {team.openConversations} open
              {team.unassignedConversations > 0 && (
                <span className="ml-1 text-amber-600 dark:text-amber-400 font-medium">
                  ({team.unassignedConversations} unassigned)
                </span>
              )}
            </span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Longest: {formatMinutes(team.longestWaitMinutes)}
            </span>
            <CoverageBadge status={team.coverageStatus ?? "covered"} unassigned={team.unassignedConversations ?? 0} />
          </div>
        </div>
        <div className="text-right ml-2">
          <div className="text-xl font-bold text-green-600 dark:text-green-400">
            {team.agentsOnline}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">online</div>
        </div>
      </div>

      {/* Mini stat row */}
      <div className="flex gap-4 text-xs text-slate-500 dark:text-slate-400">
        <span>
          <span className="font-medium text-green-600 dark:text-green-400">{team.agentsAvailable}</span> available
        </span>
        <span>
          <span className="font-medium text-red-500 dark:text-red-400">{team.agentsBusy}</span> busy
        </span>
        <span>avg {formatMinutes(team.avgWaitMinutes)}</span>
      </div>

      {/* Agent list */}
      {hasAgents && (
        <div>
          <button
            className="text-xs font-medium mb-1 text-blue-500 dark:text-blue-400 hover:underline"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? "Hide agents" : `Show ${team.agents.length} agents`}
          </button>
          {expanded && (
            <div className="max-h-52 overflow-y-auto pr-1">
              {team.agents.map((agent) => (
                <AgentRow key={agent.userId} agent={agent} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Threshold Settings Panel ───────────────────────────────────────────────────

function ThresholdSettings({
  showWarnings, setShowWarnings,
  yellowMins, setYellowMins,
  redMins, setRedMins,
  onClose
}: {
  showWarnings: boolean; setShowWarnings: (v: boolean) => void;
  yellowMins: number; setYellowMins: (v: number) => void;
  redMins: number; setRedMins: (v: number) => void;
  onClose: () => void;
}) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-200">Queue Time Alert Settings</h4>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xs">
          Close
        </button>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            checked={showWarnings}
            onChange={e => setShowWarnings(e.target.checked)}
            className="rounded"
          />
          Show queue time warnings on team cards
        </label>
      </div>
      {showWarnings && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">
              Yellow warning after (minutes)
            </label>
            <input
              type="number"
              min={1}
              max={redMins - 1}
              value={yellowMins}
              onChange={e => setYellowMins(Math.max(1, parseInt(e.target.value) || 5))}
              className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-red-500 dark:text-red-400 mb-1">
              Red warning after (minutes)
            </label>
            <input
              type="number"
              min={yellowMins + 1}
              value={redMins}
              onChange={e => setRedMins(Math.max(yellowMins + 1, parseInt(e.target.value) || 10))}
              className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const PREFS_KEY = "dashboard_queue_prefs";

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return JSON.parse(raw) as { showWarnings: boolean; yellowMins: number; redMins: number };
  } catch {}
  return { showWarnings: true, yellowMins: 5, redMins: 10 };
}

// Coverage sort order: problems first
const COVERAGE_ORDER: Record<string, number> = { no_agents: 0, no_coverage: 1, covered: 2 };

export default function Dashboard() {
  const { data, streamStatus, lastUpdated } = useLiveDashboard();

  const prefs = loadPrefs();
  const [showWarnings, setShowWarnings] = React.useState(prefs.showWarnings);
  const [yellowMins, setYellowMins] = React.useState(prefs.yellowMins);
  const [redMins, setRedMins] = React.useState(prefs.redMins);
  const [showSettings, setShowSettings] = React.useState(false);
  const [teamSearch, setTeamSearch] = React.useState("");

  // Persist preferences to localStorage
  React.useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ showWarnings, yellowMins, redMins }));
    } catch {}
  }, [showWarnings, yellowMins, redMins]);

  const longestWait = data?.kpis?.longestWaitMinutes ?? 0;
  const waitWarn = showWarnings && longestWait > yellowMins;

  return (
    <div className="p-3 sm:p-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
      {/* Connection bar */}
      <ConnectionBar
        status={streamStatus}
        lastUpdated={lastUpdated}
        isManagerView={data?.isManagerView ?? false}
      />

      {/* Settings panel */}
      {showSettings && (
        <ThresholdSettings
          showWarnings={showWarnings} setShowWarnings={setShowWarnings}
          yellowMins={yellowMins} setYellowMins={setYellowMins}
          redMins={redMins} setRedMins={setRedMins}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* KPI strip — row 1: agent + queue counts */}
      {data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-3">
            <KpiCard label="Online" value={data.kpis.agentsOnline} color="green"
              tip="Agents with a status other than Offline right now." />
            <KpiCard label="Available" value={data.kpis.agentsAvailable} color="green"
              tip="Agents whose status is Available — they can accept new chats." />
            <KpiCard label="Busy" value={data.kpis.agentsBusy}
              tip="Agents who are logged in but not Available (at capacity, custom status, etc.)." />
            <KpiCard label="Open Chats" value={data.kpis.openConversations}
              tip="All open conversations for this site — includes bot-active, async (email/ticket), and human-assigned. Broader than 'In Queue'." />
            <KpiCard label="In Queue" value={data.kpis.queuedConversations}
              warn={data.kpis.queuedConversations > 0}
              sub="waiting for agent"
              tip="Conversations actively waiting for a human agent: open + unassigned + no active bot + real-time channel only. Email and ticket are excluded because they are not time-sensitive in the same way." />
            <KpiCard label="Bot Active" value={data.kpis.botActive} color="blue" sub="handled by bot"
              tip="Open conversations being handled by a bot with no human agent assigned. These are NOT in the queue — the bot is serving the customer." />
          </div>
          {/* KPI strip — row 2: today's outcomes + timing */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
            <KpiCard label="Resolved Today" value={data.kpis.resolvedToday} color="green"
              tip="Conversations resolved since midnight UTC today. May differ from historical reports if your team is in a different timezone — use the Reports page for timezone-aware totals." />
            <KpiCard
              label="Missed / Abandoned"
              value={data.kpis.missedAbandonedToday}
              color={data.kpis.missedAbandonedToday > 0 ? "red" : undefined}
              sub="today"
              tip="Conversations that ended without an agent connecting: missed (agent/routing issue) or abandoned (visitor left the queue). Resets at midnight UTC." />
            <KpiCard
              label="Longest Wait"
              value={formatMinutes(longestWait)}
              warn={waitWarn}
              sub="queue only"
              tip="Longest current wait among conversations in the human queue. Excludes bots, email/ticket, re-queued conversations, and any conversation open longer than 60 minutes (orphaned). Uses QueuedAt — the moment it entered the human queue — not when the conversation opened." />
            <KpiCard
              label="Avg Wait"
              value={formatMinutes(data.kpis.avgWaitMinutes)}
              sub="queue only"
              tip="Average wait across first-time queue entries on real-time channels. Lower than historical reports because bot handling time and async channels are excluded. If your bots collect info for 3 minutes before routing, those 3 minutes do not count here." />
            <KpiCard
              label="Avg Handle Time"
              value={formatMinutes(data.kpis.avgHandleTimeMinutes)}
              sub="resolved today"
              tip="Average time from conversation start to resolution, for conversations resolved today. Only conversations with a resolved timestamp are included — open conversations are excluded because they have not ended yet." />
            <KpiCard
              label="Avg First Response"
              value={formatMinutes(data.kpis.avgFirstResponseMinutes)}
              sub="resolved today"
              tip="Average time from conversation start to the first agent message, for conversations resolved today. Note: this uses StartTimestamp (full picture), so it includes bot handling time. Historical reports use the same calculation." />
          </div>
        </>
      ) : (
        <>
          {[0, 1].map((row) => (
            <div key={row} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-lg p-4 flex flex-col bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 animate-pulse">
                  <div className="h-2.5 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                  <div className="h-7 w-10 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
              ))}
            </div>
          ))}
          <div className="mb-6" />
          <div className="text-center text-sm text-slate-400 dark:text-slate-500 mb-6 flex items-center justify-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-slate-400 animate-pulse" />
            {streamStatus === "connecting" ? "Connecting to live data..." : "Reconnecting..."}
          </div>
        </>
      )}

      {/* Coverage summary strip — shown when there are gaps */}
      {data && (() => {
        const gaps = data.teams.filter(t => t.coverageStatus !== "covered");
        if (gaps.length === 0) return null;
        const noAgents  = gaps.filter(t => t.coverageStatus === "no_agents").length;
        const allBusy   = gaps.filter(t => t.coverageStatus === "no_coverage").length;
        return (
          <div className="rounded-lg px-4 py-3 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 flex flex-wrap items-center gap-3">
            <span className="font-semibold text-red-700 dark:text-red-300 text-sm" aria-live="polite">
              Coverage gaps detected
            </span>
            {noAgents > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700">
                {noAgents} team{noAgents > 1 ? "s" : ""} with no agents online
              </span>
            )}
            {allBusy > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700">
                {allBusy} team{allBusy > 1 ? "s" : ""} with all agents busy
              </span>
            )}
            <span className="text-xs text-red-500 dark:text-red-400 ml-auto">
              New chats may be missed. Check routing rules or unavailability workflows.
            </span>
          </div>
        );
      })()}

      {/* Secondary widgets: top agents + channel breakdown */}
      {data && (data.topAgents?.length > 0 || data.channelBreakdown?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {data.topAgents?.length > 0 && <TopAgentsWidget agents={data.topAgents} />}
          {data.channelBreakdown?.length > 0 && <ChannelBreakdownWidget channels={data.channelBreakdown} />}
        </div>
      )}

      {/* Methodology note */}
      {data && (
        <div className="mt-6 rounded-lg px-4 py-3 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400">
          <span className="font-medium text-slate-600 dark:text-slate-300">How these numbers are calculated&nbsp;&mdash;</span>{" "}
          Wait times cover only real-time channels (web, SMS, WhatsApp, etc.) waiting for a human agent for the first time.
          Email, tickets, bot-active conversations, re-queued conversations, and any conversation open longer than 60 minutes are excluded.
          Wait time uses <em>QueuedAt</em> (when the conversation entered the human queue), not <em>StartTimestamp</em> (when it opened),
          so bot handling time before handoff does not inflate the figure.
          "Resolved Today" and timing metrics reset at midnight UTC.
          {" "}<strong>Historical reports</strong> include all channels and use StartTimestamp, so live dashboard values will often be lower — both are correct, they answer different questions.
          Hover any metric label for a full explanation.
        </div>
      )}

      {/* Team cards header */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
          Teams {data && data.teams.length > 6 && `(${data.teams.length})`}
        </h2>
        <div className="flex items-center gap-2 flex-1 justify-end">
          {/* Team search — only shown when there are more than 6 teams */}
          {data && data.teams.length > 6 && (
            <input
              type="search"
              value={teamSearch}
              onChange={e => setTeamSearch(e.target.value)}
              placeholder="Filter teams…"
              aria-label="Filter teams by name"
              className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 w-48"
            />
          )}
          <button
            onClick={() => setShowSettings(v => !v)}
            className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1"
            title="Configure queue time alerts"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
            Alert settings
          </button>
        </div>
      </div>

      {/* Team cards */}
      {data ? (
        data.teams.length === 0 ? (
          <div className="rounded-lg p-6 sm:p-12 text-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500">
            No teams found.
          </div>
        ) : (() => {
          const filtered = data.teams
            .filter(t => !teamSearch || t.teamName.toLowerCase().includes(teamSearch.toLowerCase()))
            .sort((a, b) =>
              (COVERAGE_ORDER[a.coverageStatus ?? "covered"] ?? 2) -
              (COVERAGE_ORDER[b.coverageStatus ?? "covered"] ?? 2)
            );
          return filtered.length === 0 ? (
            <div className="rounded-lg p-8 text-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 text-sm">
              No teams match "{teamSearch}"
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((team) => (
                <TeamCard
                  key={team.teamId}
                  team={team}
                  warnYellowMins={yellowMins}
                  warnRedMins={redMins}
                  showWarnings={showWarnings}
                />
              ))}
            </div>
          );
        })()
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-lg p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 animate-pulse flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
                  <div className="h-3 w-36 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
                <div className="space-y-1 text-right">
                  <div className="h-6 w-6 bg-slate-200 dark:bg-slate-700 rounded ml-auto" />
                  <div className="h-2.5 w-10 bg-slate-200 dark:bg-slate-700 rounded ml-auto" />
                </div>
              </div>
              <div className="flex gap-4">
                <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-3 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-3 w-14 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
              <div className="space-y-1.5">
                <div className="h-3 w-full bg-slate-100 dark:bg-slate-700/50 rounded" />
                <div className="h-3 w-full bg-slate-100 dark:bg-slate-700/50 rounded" />
                <div className="h-3 w-3/4 bg-slate-100 dark:bg-slate-700/50 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
